import { useState } from 'react';
import { useTranslation } from 'react-i18next';

declare global {
  interface Window {
    paypal?: any;
  }
}

export default function GiveSection() {
  const { t } = useTranslation('common');
  const [showDonationModal, setShowDonationModal] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState('');
  const [donorName, setDonorName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const impactAreas = [
    {
      icon: 'ri-group-line',
      title: t('give.impactArea1Title'),
      description: t('give.impactArea1Desc'),
    },
    {
      icon: 'ri-book-2-line',
      title: t('give.impactArea2Title'),
      description: t('give.impactArea2Desc'),
    },
    {
      icon: 'ri-global-line',
      title: t('give.impactArea3Title'),
      description: t('give.impactArea3Desc'),
    },
  ];

  const donationAmounts = [25, 50, 100, 250, 500];

  const handleAmountSelect = (amount: number) => {
    setSelectedAmount(amount);
    setCustomAmount('');
  };

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      setCustomAmount(value);
      setSelectedAmount(null);
    }
  };

  const getFinalAmount = () => {
    if (customAmount) return parseFloat(customAmount);
    return selectedAmount || 0;
  };

  const handleDonate = async () => {
    const amount = getFinalAmount();
    
    if (!amount || amount < 1) {
      alert('Please select or enter a donation amount');
      return;
    }

    if (!donorEmail || !/\S+@\S+\.\S+/.test(donorEmail)) {
      alert('Please enter a valid email address');
      return;
    }

    setIsProcessing(true);

    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      
      // Create PayPal order
      const response = await fetch(`${supabaseUrl}/functions/v1/paypal-create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount,
          donorName: donorName || 'Anonymous',
          donorEmail,
          message,
        }),
      });

      const data = await response.json();

      // Log the response for debugging
      console.log('PayPal Create Order Response:', data);

      if (!response.ok || !data.orderId) {
        console.error('Order creation failed:', data);
        const errorMessage = data.error || data.details?.message || 'Failed to create order';
        alert(`결제 처리 중 오류가 발생했습니다: ${errorMessage}`);
        setIsProcessing(false);
        return;
      }

      // Load PayPal SDK if not already loaded
      if (!window.paypal) {
        await loadPayPalScript();
      }

      // Open PayPal payment window
      window.paypal.Buttons({
        createOrder: () => data.orderId,
        onApprove: async (orderData: any) => {
          // Capture the payment
          const captureResponse = await fetch(`${supabaseUrl}/functions/v1/paypal-capture-order`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              orderId: orderData.orderID,
            }),
          });

          const captureData = await captureResponse.json();

          if (captureData.success) {
            setIsProcessing(false);
            setShowDonationModal(false);
            setShowSuccess(true);
            resetForm();
            
            setTimeout(() => {
              setShowSuccess(false);
            }, 5000);
          } else {
            throw new Error('Payment capture failed');
          }
        },
        onError: (err: any) => {
          console.error('PayPal error:', err);
          alert('결제가 실패했습니다. 다시 시도해주세요.');
          setIsProcessing(false);
        },
      }).render('#paypal-button-container');

    } catch (error) {
      console.error('Error:', error);
      alert('오류가 발생했습니다. 다시 시도해주세요.');
      setIsProcessing(false);
    }
  };

  const loadPayPalScript = () => {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://www.paypal.com/sdk/js?client-id=ASocMJkh8KQB6-8O1QqU_yWz81n6hmv7-rgCpxIjMyBFVm8YKS2sh5aMTlgSfKPXCBC0jOVmSo3j1eOy&currency=USD';
      script.onload = resolve;
      script.onerror = reject;
      document.body.appendChild(script);
    });
  };

  const resetForm = () => {
    setSelectedAmount(null);
    setCustomAmount('');
    setDonorName('');
    setDonorEmail('');
    setMessage('');
  };

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="give" className="py-32 px-6 bg-zinc-950 relative overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="inline-block px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full mb-6">
              <span className="text-amber-500 text-sm font-semibold uppercase tracking-wider">{t('give.badge')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-8 leading-tight">
              {t('give.title')}
            </h2>
            <p className="text-xl text-gray-300 mb-12 leading-relaxed">
              {t('give.subtitle')}
            </p>

            <div className="space-y-6 mb-12">
              {impactAreas.map((area, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-amber-500/10 rounded-lg flex-shrink-0">
                    <i className={`${area.icon} text-xl text-amber-500`}></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold mb-2">{area.title}</h3>
                    <p className="text-gray-400">{area.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <button 
                onClick={() => setShowDonationModal(true)}
                className="px-8 py-4 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                {t('give.donateBtn')}
              </button>
              <button 
                onClick={scrollToContact}
                className="px-8 py-4 bg-transparent border-2 border-white/20 hover:border-white text-white font-semibold rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                {t('give.partnerBtn')}
              </button>
            </div>
          </div>

          <div className="relative">
            <div className="relative w-full h-[600px] rounded-2xl overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/5f3cb8f8a5418cbcad9941ed33716b30.jpg"
                alt="Community Support"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-transparent to-transparent"></div>
            </div>

            <div className="absolute -bottom-8 left-0 right-0 lg:left-auto lg:right-auto lg:-left-8 mx-4 lg:mx-0 bg-amber-500 rounded-2xl p-6 lg:p-8 max-w-sm">
              <p className="text-black font-bold text-base lg:text-lg mb-2">
                {t('give.impactNote')}
              </p>
              <p className="text-black/70 text-sm">
                {t('give.impactNoteDesc')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Donation Modal */}
      {showDonationModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-zinc-900 border-b border-white/10 p-6 flex justify-between items-center">
              <h3 className="text-2xl font-bold">{t('give.modalTitle')}</h3>
              <button 
                onClick={() => {
                  setShowDonationModal(false);
                  resetForm();
                }}
                className="w-10 h-10 flex items-center justify-center hover:bg-white/10 rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Amount Selection */}
              <div>
                <label className="block text-sm font-semibold mb-3">{t('give.selectAmount')}</label>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {donationAmounts.map((amount) => (
                    <button
                      key={amount}
                      onClick={() => handleAmountSelect(amount)}
                      className={`py-3 rounded-lg font-semibold transition-all cursor-pointer whitespace-nowrap ${
                        selectedAmount === amount
                          ? 'bg-amber-500 text-black'
                          : 'bg-white/5 hover:bg-white/10 text-white'
                      }`}
                    >
                      ${amount}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  placeholder={t('give.customAmount')}
                  value={customAmount}
                  onChange={handleCustomAmountChange}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-amber-500 transition-colors text-sm"
                />
              </div>

              {/* Donor Information */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">{t('give.donorName')}</label>
                  <input
                    type="text"
                    placeholder={t('give.donorNamePlaceholder')}
                    value={donorName}
                    onChange={(e) => setDonorName(e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-amber-500 transition-colors text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">{t('give.donorEmail')}</label>
                  <input
                    type="email"
                    placeholder={t('give.donorEmailPlaceholder')}
                    value={donorEmail}
                    onChange={(e) => setDonorEmail(e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-amber-500 transition-colors text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">{t('give.message')}</label>
                  <textarea
                    placeholder={t('give.messagePlaceholder')}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-amber-500 transition-colors resize-none text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">{message.length}/500</p>
                </div>
              </div>

              {/* PayPal Button Container */}
              <div id="paypal-button-container"></div>

              {/* Donate Button */}
              <button
                onClick={handleDonate}
                disabled={isProcessing || (!selectedAmount && !customAmount)}
                className={`w-full py-4 rounded-lg font-semibold transition-all whitespace-nowrap ${
                  isProcessing || (!selectedAmount && !customAmount)
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'bg-amber-500 hover:bg-amber-600 text-black cursor-pointer'
                }`}
              >
                {isProcessing ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    {t('give.processing')}
                  </span>
                ) : (
                  `${t('give.donateButton')}${getFinalAmount()}`
                )}
              </button>

              <p className="text-xs text-gray-500 text-center">
                {t('give.paypalNote')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {showSuccess && (
        <div className="fixed bottom-8 right-8 bg-green-500 text-white px-6 py-4 rounded-lg shadow-2xl z-50 flex items-center gap-3">
          <i className="ri-check-line text-2xl"></i>
          <div>
            <p className="font-semibold">{t('give.successTitle')}</p>
            <p className="text-sm text-white/80">{t('give.successMessage')}</p>
          </div>
        </div>
      )}
    </section>
  );
}
